/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React from "react";

import styled from "styled-components";

import { BlockType } from "@vienna/common";

import { Block } from "src/classes/block";
import {
  CodeBlock,
  ColorBlock,
  FileBlock,
  ImageBlock,
  LinkBlock,
  PlateWindowBlock,
  TextBlock,
} from "src/classes/block/block-types";

interface Props {
  block: Block;
}
export function Actions({ block }: Props) {
  return (
    <div>
      {(() => {
        switch (block.snapshot.type) {
          case BlockType.Text:
            const textBlock = block as TextBlock;
            return (
              <>
                <StyledAction>
                  <StyledExternalLink target="_blank" rel="noopener noreferrer" href={textBlock.searchLink}>
                    Search Google
                  </StyledExternalLink>
                </StyledAction>
                <StyledAction onClick={() => textBlock.copyText()}>Copy Text</StyledAction>
              </>
            );
          case BlockType.Image:
            const imageBlock = block as ImageBlock;
            return (
              <>
                <StyledAction>
                  <StyledExternalLink
                    href={imageBlock.downloadLink || ""}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Download
                  </StyledExternalLink>
                </StyledAction>
                <StyledAction>
                  <StyledExternalLink href={imageBlock.getImageLinkForSize("biggest")} target="_blank">
                    Open in New Tab
                  </StyledExternalLink>
                </StyledAction>
                <StyledAction>
                  <StyledExternalLink href={imageBlock.searchLink || ""} target="_blank" rel="noopener noreferrer">
                    Reverse Image Search
                  </StyledExternalLink>
                </StyledAction>
              </>
            );
          case BlockType.File:
            let fileBlock = block as FileBlock;
            return (
              <StyledAction>
                <StyledExternalLink href={fileBlock.fileURL} download target="_self">
                  Download {fileBlock.readableFileSize}
                </StyledExternalLink>
              </StyledAction>
            );
          case BlockType.Link:
            const linkBlock = block as LinkBlock;
            return (
              <StyledAction>
                <StyledExternalLink href={linkBlock.snapshot.url} target="_blank" rel="noopener noreferrer">
                  Open in New Tab
                </StyledExternalLink>
              </StyledAction>
            );
          case BlockType.Code:
            const codeBlock = block as CodeBlock;
            return <StyledAction onClick={() => codeBlock.copyText()}>Copy Text</StyledAction>;
          case BlockType.Color:
            const colorBlock = block as ColorBlock;
            return <StyledAction onClick={() => colorBlock.copyHex()}>Copy Hex</StyledAction>;
          case BlockType.PlateWindow:
            const plateWindow = block as PlateWindowBlock;
            return <StyledAction onClick={() => plateWindow.openInNewTab()}>Open in New Tab</StyledAction>;
          default:
            return null;
        }
      })()}
    </div>
  );
}

const StyledAction = styled.div`
  color: ${({ theme }) => theme.text.subtle};
  user-select: none;
  margin-bottom: 6px;
  :hover {
    color: ${({ theme }) => theme.text.url};
  }
  cursor: pointer;
`;

const StyledExternalLink = styled.a`
  text-decoration: none;
  color: inherit;
`;
